import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../css/Donantes.css';
import { useUser } from '../lib/UserContext';
import { PERMISSIONS, hasPermission } from '../lib/permissions';

function Donantes() {
  const [donantes, setDonantes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [tiposIdentificacion, setTiposIdentificacion] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    id: 0,
    identificacion: '',
    nombres: '',
    apellidos: '',
    id_Rol: '',
    id_tipoIdentificacion: ''
  });
  const [editingId, setEditingId] = useState(null);
  
  // Obtener el usuario actual y su rol
  const { getUserRoleId, isTesorero } = useUser();
  const userRoleId = getUserRoleId();

  // Verificar permisos
  const canCreate = hasPermission(userRoleId, PERMISSIONS.DONANTES.CREATE);
  const canEdit = hasPermission(userRoleId, PERMISSIONS.DONANTES.EDIT);
  const canDelete = hasPermission(userRoleId, PERMISSIONS.DONANTES.DELETE);
  const canView = hasPermission(userRoleId, PERMISSIONS.DONANTES.VIEW);

  // 🔍 DEBUG: Logs para depuración
  useEffect(() => {
    console.log('=== DEBUG DONANTES - INFORMACIÓN DE USUARIO ===');
    console.log('userRoleId:', userRoleId);
    console.log('isTesorero:', isTesorero());
    console.log('Permisos:');
    console.log('  - canCreate:', canCreate);
    console.log('  - canEdit:', canEdit);
    console.log('  - canDelete:', canDelete);
    console.log('  - canView:', canView);
    console.log('PERMISSIONS.DONANTES.CREATE:', PERMISSIONS.DONANTES.CREATE);
    console.log('PERMISSIONS.DONANTES.EDIT:', PERMISSIONS.DONANTES.EDIT);
    console.log('PERMISSIONS.DONANTES.DELETE:', PERMISSIONS.DONANTES.DELETE);
    console.log('PERMISSIONS.DONANTES.VIEW:', PERMISSIONS.DONANTES.VIEW);
    console.log('===============================================');
  }, [userRoleId, canCreate, canEdit, canDelete, canView, isTesorero]);

  // baseURL ya configurada en api

  // Cargar roles disponibles
  const fetchRoles = useCallback(async () => {
    try {
      const response = await api.get(`/Donantes/Roles`);
      const data = Array.isArray(response.data) ? response.data : [];
      setRoles(data);
      return data;
    } catch (error) {
      console.error('Error al cargar roles:', error);
      toast.error('Error al cargar roles');
      return [];
    }
  }, []);

  // Cargar tipos de identificación disponibles
  const fetchTiposIdentificacion = useCallback(async () => {
    try {
      const response = await api.get(`/Donantes/TiposIdentificacion`);
      const data = Array.isArray(response.data) ? response.data : [];
      setTiposIdentificacion(data);
      return data;
    } catch (error) {
      console.error('Error al cargar tipos de identificación:', error);
      toast.error('Error al cargar tipos de identificación');
      return [];
    }
  }, []);

  // Carga del listado de donantes (memoizada para usar en efectos y acciones)
  const fetchDonantes = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔍 Cargando donantes desde el API...');
      
      // Intenta rutas conocidas del API en orden de probabilidad
      let response;
      try {
        // Endpoint estándar GET para listar todos los donantes
        console.log('📡 Intentando GET /Donantes');
        response = await api.get(`/Donantes`);
        console.log('✅ Respuesta exitosa del API');
      } catch (err1) {
        if (err1?.response?.status === 404) {
          try {
            // Posible convención GetAll
            console.log('📡 Intentando GET /Donantes/GetAll');
            response = await api.get(`/Donantes/GetAll`);
            console.log('✅ Respuesta exitosa del API (GetAll)');
          } catch (err2) {
            if (err2?.response?.status === 404) {
              // Último intento: lista directa
              console.log('📡 Intentando GET /Donantes/Lista');
              response = await api.get(`/Donantes/Lista`);
              console.log('✅ Respuesta exitosa del API (Lista)');
            } else {
              throw err2;
            }
          }
        } else {
          throw err1;
        }
      }
      
      console.log('📦 Respuesta completa del API:', response);
      console.log('📦 response.data:', response.data);
      console.log('📦 Tipo de response.data:', typeof response.data);
      console.log('📦 Es array?:', Array.isArray(response.data));
      
      const data = Array.isArray(response.data) ? response.data : (response.data?.donantes ?? []);
      
      console.log('👥 DONANTES PROCESADOS:');
      console.log('   - Total de registros:', data.length);
      console.log('   - Datos:', data);
      
      if (data.length > 0) {
        console.log('   - Primer donante:', data[0]);
        console.table(data);
      }
      
      setDonantes(data);
      console.log('✅ Donantes cargados en el estado');
    } catch (error) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message || error?.message || 'Error desconocido';
      toast.error(`Error al cargar los donantes${status ? ` (HTTP ${status})` : ''}`);
      console.error('❌ Error al cargar donantes:', { status, message, error });
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar donantes, roles y tipos de identificación al montar el componente
  useEffect(() => {
    fetchRoles();
    fetchTiposIdentificacion();
    fetchDonantes();
  }, [fetchRoles, fetchTiposIdentificacion, fetchDonantes]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Verificar permisos antes de proceder
    if (editingId && !canEdit) {
      toast.error('No tiene permisos para editar usuarios');
      return;
    }
    
    if (!editingId && !canCreate) {
      toast.error('No tiene permisos para crear usuarios');
      return;
    }

    setLoading(true);

    try {
      // Obtener el primer rol y tipo disponibles si no se especificó
      const defaultRolId = roles.length > 0 ? roles[0].id : null;
      const defaultTipoId = tiposIdentificacion.length > 0 ? tiposIdentificacion[0].id : null;

      const submitData = {
        id: formData.id || 0,
        identificacion: parseInt(formData.identificacion) || 0,
        nombres: formData.nombres || '',
        apellidos: formData.apellidos || '',
        id_Rol: parseInt(formData.id_Rol) || defaultRolId,
        id_tipoIdentificacion: parseInt(formData.id_tipoIdentificacion) || defaultTipoId
      };

      // Validar que los IDs requeridos estén presentes
      if (!submitData.id_Rol || !submitData.id_tipoIdentificacion) {
        toast.error('Por favor seleccione el rol y tipo de identificación');
        setLoading(false);
        return;
      }

      if (editingId) {
        await api.put(`/Donantes/Donante/${editingId}`, submitData);
        toast.success('Donante actualizado exitosamente');
      } else {
        await api.post(`/Donantes/Donante`, submitData);
        toast.success('Donante registrado exitosamente');
      }

      // Limpiar formulario y recargar lista
      resetForm();
      await fetchDonantes();
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.response?.data || error?.message || 'Error desconocido';
      toast.error(editingId ? `Error al actualizar donante: ${JSON.stringify(errorMsg)}` : 'Error al registrar donante');
      console.error('Error en submit:', { 
        status: error?.response?.status, 
        data: error?.response?.data,
        submitData: editingId ? 'UPDATE' : 'CREATE',
        formData
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (donante) => {
    // Verificar permisos
    if (!canEdit) {
      toast.error('No tiene permisos para editar usuarios');
      return;
    }

    // Usar los IDs que vienen del backend
    const defaultRolId = roles.length > 0 ? roles[0].id : '';
    const defaultTipoId = tiposIdentificacion.length > 0 ? tiposIdentificacion[0].id : '';
    
    setFormData({
      id: donante.id,
      identificacion: donante.identificacion.toString(),
      nombres: donante.nombres,
      apellidos: donante.apellidos,
      id_Rol: donante.id_Rol || donante.Id_Rol || defaultRolId,
      id_tipoIdentificacion: donante.id_tipoIdentificacion || defaultTipoId
    });
    setEditingId(donante.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    // Verificar permisos
    if (!canDelete) {
      toast.error('No tiene permisos para eliminar usuarios');
      return;
    }

    if (!window.confirm('¿Está seguro de eliminar este donante?')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/Donantes/Donante/${id}`);
      toast.success('Donante eliminado exitosamente');
      await fetchDonantes();
    } catch (error) {
      toast.error('Error al eliminar donante');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReiniciarPassword = async (donante) => {
    // Solo el administrador puede reiniciar contraseñas
    if (userRoleId !== 1) { // 1 = ADMINISTRADOR
      toast.error('Solo el administrador puede reiniciar contraseñas');
      return;
    }

    // Verificar que el usuario tiene rol Administrador o Tesorero
    const rolId = donante.id_Rol || donante.Id_Rol;
    if (rolId !== 1 && rolId !== 3) { // 1 = Admin, 3 = Tesorero
      toast.info('Solo se puede reiniciar contraseñas de Administradores y Tesoreros');
      return;
    }

    const mensaje = `¿Está seguro de reiniciar la contraseña de ${donante.nombres} ${donante.apellidos}?\n\nLa nueva contraseña será: ${donante.identificacion}\nEl usuario deberá cambiarla en su próximo login.`;
    
    if (!window.confirm(mensaje)) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.post(`/Donantes/reiniciar-password/${donante.identificacion}`);
      toast.success(response.data.mensaje || 'Contraseña reiniciada exitosamente');
      await fetchDonantes();
    } catch (error) {
      const errorMsg = error?.response?.data?.mensaje || 'Error al reiniciar contraseña';
      toast.error(errorMsg);
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    const defaultRolId = roles.length > 0 ? roles[0].id : '';
    const defaultTipoId = tiposIdentificacion.length > 0 ? tiposIdentificacion[0].id : '';
    
    setFormData({
      id: 0,
      identificacion: '',
      nombres: '',
      apellidos: '',
      id_Rol: defaultRolId,
      id_tipoIdentificacion: defaultTipoId
    });
    setEditingId(null);
  };

  const handleGoBack = () => {
    window.location.href = '/opciones';
  };

  // Si no tiene permiso para ver, mostrar mensaje
  if (!canView) {
    return (
      <div className="donantes-container">
        <ToastContainer position="top-right" autoClose={3000} />
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Acceso Denegado</h2>
          <p>No tiene permisos para ver esta sección.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="donantes-container">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="donantes-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <h1 className="donantes-title">Gestión de Donantes</h1>
            <p className="donantes-subtitle">Registre y administre los donantes de la iglesia</p>
          </div>
          <button 
            type="button" 
            onClick={handleGoBack}
            className="btn-back"
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'background-color 0.3s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#5a6268'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#6c757d'}
          >
            <i className="fas fa-arrow-left"></i>
            Volver
          </button>
        </div>
        {isTesorero() && (
          <div style={{ 
            backgroundColor: '#d1ecf1', 
            border: '1px solid #0c5460', 
            borderRadius: '4px',
            padding: '0.75rem', 
            marginTop: '0.5rem',
            color: '#0c5460'
          }}>
            <i className="fas fa-info-circle" style={{ marginRight: '0.5rem' }}></i>
            <strong>Modo Tesorero:</strong> Puede ver, crear y editar usuarios. Solo el Administrador puede eliminar usuarios.
          </div>
        )}
      </div>

      {/* Mostrar formulario solo si tiene permisos de crear o editar */}
      {(canCreate || canEdit) && (
        <form className="donantes-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="identificacion">Número de Identificación:</label>
          <input
            type="number"
            id="identificacion"
            name="identificacion"
            value={formData.identificacion}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="nombres">Nombres:</label>
          <input
            type="text"
            id="nombres"
            name="nombres"
            value={formData.nombres}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="apellidos">Apellidos:</label>
          <input
            type="text"
            id="apellidos"
            name="apellidos"
            value={formData.apellidos}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="id_Rol">Rol:</label>
          <select
            id="id_Rol"
            name="id_Rol"
            value={formData.id_Rol}
            onChange={handleInputChange}
            required
          >
            <option value="">Seleccione un rol</option>
            {roles.map(rol => (
              <option key={rol.id} value={rol.id}>
                {rol.rol}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="id_tipoIdentificacion">Tipo de Identificación:</label>
          <select
            id="id_tipoIdentificacion"
            name="id_tipoIdentificacion"
            value={formData.id_tipoIdentificacion}
            onChange={handleInputChange}
            required
          >
            <option value="">Seleccione un tipo</option>
            {tiposIdentificacion.map(tipo => (
              <option key={tipo.id} value={tipo.id}>
                {tipo.tipo_Identificacion}
              </option>
            ))}
          </select>
        </div>

        <div className="btn-container">
          <button type="submit" className="btn btn-primary">
            {editingId ? 'Actualizar' : 'Registrar'}
          </button>
          {editingId && (
            <button type="button" className="btn btn-secondary" onClick={resetForm}>
              Cancelar
            </button>
          )}
        </div>
      </form>
      )}

      <div className="table-wrapper">
        {(!loading && (!donantes || donantes.length === 0)) ? (
          <div style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
            No hay donantes registrados.
          </div>
        ) : (
          <table className="donantes-table">
            <thead>
              <tr>
                <th>Identificación</th>
                <th>Tipo ID</th>
                <th>Nombres</th>
                <th>Apellidos</th>
                <th>Rol</th>
                {(canEdit || canDelete) && <th>Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {donantes.map(donante => (
                <tr key={donante.id}>
                  <td>{donante.identificacion}</td>
                  <td>{donante.tipoIdentificacion || 'N/A'}</td>
                  <td>{donante.nombres}</td>
                  <td>{donante.apellidos}</td>
                  <td>{donante.rol || 'N/A'}</td>
                  {(canEdit || canDelete) && (
                    <td>
                      {canEdit && (
                        <button 
                          className="btn-edit" 
                          onClick={() => handleEdit(donante)}
                          title="Editar donante"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                      )}
                      {canDelete && (
                        <button 
                          className="btn-delete"
                          onClick={() => handleDelete(donante.id)}
                          title="Eliminar donante"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      )}
                      {userRoleId === 1 && (donante.id_Rol === 1 || donante.id_Rol === 3 || donante.Id_Rol === 1 || donante.Id_Rol === 3) && (
                        <button 
                          className="btn-reset-password"
                          onClick={() => handleReiniciarPassword(donante)}
                          title="Reiniciar contraseña"
                        >
                          <i className="fas fa-key"></i>
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
}

export default Donantes;